import React, { useState, useEffect, createContext, useContext } from "react";
import { useParams, useNavigate, Link, Routes, Route } from "react-router-dom";
import { API_BASE } from "../config";

import Committee from "./Committee";
import Speakers from "./Speakers";
import Awards from "./Awards";
import Venue from "./ Venue";
import Contact from "./Contact";
import GlobalContact from "./globalcontact"; // ✅ ADDED
import Register from "./Register";
import CallForAbstracts from "./CallForAbstracts";
import CodeOfConduct from "./CodeOfConduct";
import PrivacyPolicy from "./PrivacyPolicy";
import Topics from "./Topics";
import Abstract from "./Abstract";
import { App as GlobalHome } from "../global/Globalhome";
import AbstractSubmission from "./AbstractSubmission";
import Events from "./Events";
import Globalsponser from "../global/Globalsponser";
import AboutInformation from "../global/AboutInformation";
import RegistrationFeePage from '../components/Registrationfee';
import RegistrationPoliciesPage from '../components/RegistrationPoliciesPage';
import ProgrammeOverview from '../components/ProgrammeOverview';
import ScheduleTab from '../components/SchedulePage';
import GuidelinesPage from './Guidelines';
import SummitPublications from '../components/summitPublications';
import SubmissionGuidelines from './SubmissionGuidelines';
import PresentationGuidelinesPage from './PresentationGuidelinesPage';

// ── Context ──────────────────────────────────────────────────
const SummitContext = createContext(null);
export const useSummit = () => useContext(SummitContext);

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-deep-navy">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent" />
        <p className="mt-4 text-gray-300">Loading summit details...</p>
      </div>
    </div>
  );
}

function ErrorScreen({ error }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-deep-navy">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="text-6xl mb-4">⚠️</div>
        <h2 className="text-2xl font-bold text-white mb-2">Error</h2>
        <p className="text-gray-300 mb-6">{error || "Event not found"}</p>
        <Link
          to="/"
          className="inline-block px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}

// ─── Helper ──────────────────────────────────────────────────
const createEventSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/--+/g, '-')
    .trim();
};

// ── Wrapper ──────────────────────────────────────────────────
function SummitHomeWrapper() {
  const { summit, eventId } = useSummit();
  return <GlobalHome summit={summit} eventId={eventId} />;
}

// ── Program Page ─────────────────────────────────────────────
function ProgramPage() {
  const { summit } = useSummit();
  const days = summit?.days || [];
  const slug = summit?.slug || createEventSlug(summit?.title);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-deep-navy">
      <div className="container mx-auto px-4 py-12">
        <Link
          to={`/${slug}`}
          className="mb-8 inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors text-sm"
        >
          ← Back to Summit Home
        </Link>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">
          Program Schedule
        </h1>
        {days.length === 0 ? (
          <div className="bg-white/5 rounded-xl p-12 text-center">
            <p className="text-gray-400">
              Program schedule is being finalized. Check back soon!
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {days.map((day, dayIdx) => (
              <div key={dayIdx} className="bg-white/5 rounded-xl overflow-hidden">
                <div className="bg-cyan-600/20 px-6 py-4 border-b border-white/10">
                  <h2 className="text-xl font-semibold text-white">
                    Day {day.dayNumber}:{" "}
                    {new Date(day.date).toLocaleDateString("en-US", {
                      weekday: "long", month: "long", day: "numeric",
                    })}
                  </h2>
                </div>
                <div className="p-6 space-y-4">
                  {day.sessions?.map((session, sessionIdx) => (
                    <div key={sessionIdx} className="border-l-2 border-cyan-400 pl-4 py-2">
                      <div className="flex flex-wrap gap-4 items-start">
                        <div className="min-w-[120px]">
                          <span className="text-cyan-400 font-semibold">
                            {new Date(session.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            {" "}-{" "}
                            {new Date(session.endTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-lg mb-1">{session.title}</h3>
                          {session.description && (
                            <p className="text-gray-400 text-sm mb-2">{session.description}</p>
                          )}
                          {session.room && (
                            <p className="text-gray-500 text-xs">📍 {session.room}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Submission Page ──────────────────────────────────────────
function SubmissionPage() {
  const { summit } = useSummit();
  const slug = summit?.slug || createEventSlug(summit?.title);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-deep-navy">
      <div className="container mx-auto px-4 py-12">
        <Link
          to={`/${slug}`}
          className="mb-8 inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors text-sm"
        >
          ← Back to Summit Home
        </Link>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">
          Call for Submissions
        </h1>
        <div className="bg-white/5 rounded-xl p-8 space-y-6">
          <div>
            <h3 className="text-cyan-400 font-semibold mb-2">Submission Guidelines</h3>
            <p className="text-gray-300">Submit your papers and presentations for {summit?.title}.</p>
          </div>
          <div>
            <h3 className="text-cyan-400 font-semibold mb-2">Important Dates</h3>
            <p className="text-gray-300">
              Submission Deadline: {new Date(summit?.date).toLocaleDateString()} - 2 months
            </p>
          </div>
          <div className="text-center pt-4">
            <Link
              to={`/${slug}/call-for-abstracts`}
              className="inline-block px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
            >
              Submit Now →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Registration Page ────────────────────────────────────────
function RegistrationPage() {
  const { summit } = useSummit();
  const slug = summit?.slug || createEventSlug(summit?.title);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-deep-navy">
      <div className="container mx-auto px-4 py-12">
        <Link
          to={`/${slug}`}
          className="mb-8 inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors text-sm"
        >
          ← Back to Summit Home
        </Link>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">Registration</h1>
        <div className="bg-white/5 rounded-xl p-8 space-y-6">
          <div>
            <h3 className="text-cyan-400 font-semibold mb-2">Registration Fees</h3>
            <p className="text-gray-300">Regular: ${summit?.price?.regular || 499}</p>
            <p className="text-gray-300">VIP: ${summit?.price?.vip || 999}</p>
          </div>
          <div className="text-center pt-4">
            <Link
              to={`/${slug}/register`}
              className="inline-block px-8 py-3 bg-gradient-to-r from-cyan-600 to-teal-600 text-white rounded-lg hover:from-cyan-700 hover:to-teal-700 transition-all transform hover:scale-105"
            >
              Register Now →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Fetch helpers ────────────────────────────────────────────
const fetchEventBySlug = async (slug) => {
  try {
    const response = await fetch(`${API_BASE}/api/slug/${slug}`);
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.event) return data.event;
    }
    // Fallback: try old events endpoint
    const fallbackResponse = await fetch(`${API_BASE}/api/events/${slug}`);
    if (fallbackResponse.ok) {
      const data = await fallbackResponse.json();
      if (data.success && data.event) return data.event;
    }
    return null;
  } catch (err) {
    console.error("Error fetching event by slug:", err);
    return null;
  }
};

const fetchEventById = async (id) => {
  try {
    const response = await fetch(`${API_BASE}/api/slug/id/${id}`);
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.event) return data.event;
    }
    return null;
  } catch (err) {
    console.error("Error fetching event by ID:", err);
    return null;
  }
};

// ── Main component ───────────────────────────────────────────
const SummitDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [summit, setSummit] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setError("No event name provided");
      setLoading(false);
      return;
    }

    const fetchEvent = async () => {
      setLoading(true);
      try {
        let cleanId = id;
        if (cleanId.includes('/')) {
          cleanId = cleanId.split('/').pop();
        }
        cleanId = cleanId.replace(/^\/+|\/+$/g, '');

        console.log(`Fetching event with slug: ${cleanId}`);

        let eventData = await fetchEventBySlug(cleanId);

        if (!eventData && /^[0-9a-fA-F]{24}$/.test(cleanId)) {
          console.log("Not found by slug, trying by ID:", cleanId);
          eventData = await fetchEventById(cleanId);
        }

        if (eventData) {
          const correctSlug = eventData.slug || createEventSlug(eventData.title);

          setSummit(eventData);
          setEventId(eventData._id);
          setError(null);

          // ✅ Redirect if slug in URL doesn't match event's slug
          if (cleanId !== correctSlug) {
            console.log(`Redirecting from ${cleanId} to ${correctSlug}`);
            const currentPath = window.location.pathname;
            const newPath = currentPath.replace(`/${cleanId}`, `/${correctSlug}`);
            navigate(newPath, { replace: true });
            return;
          }
        } else {
          setError(`Event "${cleanId}" not found.`);
        }
      } catch (err) {
        console.error("Error fetching event:", err);
        setError("Failed to load event details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id, navigate]);

  if (loading) return <LoadingScreen />;
  if (error || !summit) return <ErrorScreen error={error} />;

  const slug = summit?.slug || createEventSlug(summit?.title);

  return (
    <SummitContext.Provider value={{ summit, eventId: summit?._id || summit?.id }}>
      <Routes>
        <Route index element={<SummitHomeWrapper />} />
        <Route path="committee" element={<Committee />} />
        <Route path="speakers" element={<Speakers />} />
        <Route path="program" element={<ProgramPage />} />
        <Route path="submission" element={<SubmissionPage />} />
        <Route path="registration" element={<RegistrationPage />} />
        <Route path="sponsors" element={<Globalsponser />} />
        <Route path="awards" element={<Awards />} />
        <Route path="venue" element={<Venue />} />
        <Route path="contact" element={<Contact />} />
        <Route path="globalcontact" element={<GlobalContact />} /> {/* ✅ ADDED */}
        <Route path="register" element={<Register />} />
        <Route path="call-for-abstracts" element={<CallForAbstracts />} />
        <Route path="code-of-conduct" element={<CodeOfConduct />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="topics" element={<Topics />} />
        <Route path="events" element={<Events />} />
        <Route path="abstract" element={<Abstract />} />
        <Route path="abstract-submission" element={<AbstractSubmission />} />
        <Route path="about-information" element={<AboutInformation />} />
        <Route path="registration-fee" element={<RegistrationFeePage />} />
        <Route path="submission-guidelines" element={<SubmissionGuidelines />} />
        <Route path="registration-policies" element={<RegistrationPoliciesPage />} />
        <Route path="programme-overview" element={<ProgrammeOverview />} />
        <Route path="schedule" element={<ScheduleTab />} />
        <Route path="guidelines" element={<GuidelinesPage />} />
        <Route path="summit-publications" element={<SummitPublications />} />
        <Route path="presentation-guidelines" element={<PresentationGuidelinesPage />} />
      </Routes>
    </SummitContext.Provider>
  );
};

export default SummitDetails;